import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import { ClipboardList } from "lucide-react";
import { EmptyState, PageHeader, Skeleton, cn } from "@chotca/ui";
import { ErrorState } from "@chotca/app-shell";
import {
  useMyPostings,
  useOrgInsights,
  usePlanStatus,
} from "@chotca/api-client";
import { useAuthStore, useUiStore } from "@chotca/auth";
import type { Posting } from "@chotca/sdk";
import { nextPlanUp } from "@chotca/config";
import type { InsightTip } from "@chotca/config";
import { InsightRail } from "@/components/insight-rail";
import { PostingHealthCard } from "@/components/posting-health-card";
import { PostingQuotaCard } from "@/components/posting-quota-card";
import { useSubscriptionModals } from "@/components/employer/subscription-modals";
import {
  bucketFromHealth,
  derivePostingHealth,
  type HealthBucket,
} from "./posting-health";

const FILTERS: { value: "OPEN" | "CLOSED"; labelKey: string }[] = [
  { value: "OPEN", labelKey: "filterOpen" },
  { value: "CLOSED", labelKey: "filterClosed" },
];

// Bucket display order is the priority feed mental model — needs-attention
// first so the employer can act on it without scrolling, running middle so
// the bulk of the page feels active, closed last as an archive area.
const BUCKET_ORDER: HealthBucket[] = ["needs_attention", "running", "closed"];
const BUCKET_LABEL_KEY: Record<HealthBucket, string> = {
  needs_attention: "bucketNeedsAttention",
  running: "bucketRunning",
  closed: "bucketClosed",
};

export function PostingsListClient() {
  const t = useTranslations("postings");
  const filter = useUiStore((s) => s.postingFilter);
  const setFilter = useUiStore((s) => s.setPostingFilter);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const { data, isLoading, isError, refetch } = useMyPostings();
  // We hit /insights here too (not only inside InsightRail) because tips
  // attached to specific postings need to render inline on the matching
  // card. React Query dedupes the network call, so this isn't a 2x cost.
  const { data: insights } = useOrgInsights(activeOrgId);

  // Anti-spam quota for OPEN postings. Server is authoritative (count + tier
  // limit); the page-local counts below are only for the filter chips.
  const quota = insights?.postingQuota;
  const atLimit = quota ? quota.open >= quota.limit : false;

  // FREE orgs get a single posting slot, so the "X đang mở · Y đã đóng" count +
  // the quota card are noise — hide them for FREE.
  const plan = usePlanStatus(activeOrgId);
  const effectiveCode = plan.data?.effectiveCode ?? "FREE";
  const isFree = effectiveCode === "FREE";
  const planName = plan.data?.plan.name ?? "Miễn phí";
  // Next tier to upsell when at the posting limit; null on the top tier.
  const nextPlan = nextPlanUp(effectiveCode);
  const { openUpgrade, upgradeTo } = useSubscriptionModals();

  const allItems = data?.items ?? [];

  // Count chips on each filter come from ALL items (pre-filter), not from
  // the visible slice — the user expects "Đang mở · 3" to be a stable
  // number regardless of which chip is currently selected.
  const counts = useMemo(() => {
    return {
      OPEN: allItems.filter((p) => p.state === "OPEN").length,
      CLOSED: allItems.filter((p) => p.state !== "OPEN").length,
    };
  }, [allItems]);

  const visible = useMemo(() => {
    if (filter === "ALL") return allItems;
    if (filter === "OPEN") return allItems.filter((p) => p.state === "OPEN");
    return allItems.filter((p) => p.state !== "OPEN");
  }, [allItems, filter]);

  const buckets = useMemo(() => groupByBucket(visible), [visible]);

  // Map posting-scoped tips by postingId so each card can render its own
  // nudges without scanning the full list. Org-level tips are handled by
  // the rail and intentionally skipped here.
  const tipsByPosting = useMemo(() => {
    const map = new Map<string, InsightTip[]>();
    for (const tip of insights?.tips ?? []) {
      if (!tip.postingId) continue;
      const arr = map.get(tip.postingId) ?? [];
      arr.push(tip);
      map.set(tip.postingId, arr);
    }
    return map;
  }, [insights]);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      {/* min-w-0: on mobile the implicit grid track is `auto` (min = min-content),
          so a long posting title (h3 is `truncate` → nowrap) would stretch the
          track past the viewport and force horizontal scroll. Capping the item's
          min-width at 0 mirrors the desktop `minmax(0,1fr)` track. */}
      <div className="min-w-0 space-y-6">
        <PageHeader
          title={t("listTitle")}
          description={
            isFree
              ? undefined
              : t("subtitleCounts", {
                  open: counts.OPEN,
                  closed: counts.CLOSED,
                })
          }
          actions={
            atLimit ? (
              // Reached the plan's posting limit → the CTA opens the upgrade
              // modal instead of being a dead disabled button, so the employer
              // has a clear path to post more.
              <button
                type="button"
                onClick={() =>
                  openUpgrade({
                    reason: "posting_limit",
                    limit: quota?.limit ?? 1,
                    planName,
                  })
                }
                title={
                  nextPlan
                    ? t("quotaAtLimitDesc", {
                        limit: quota?.limit ?? 1,
                        planName,
                        nextPlan: nextPlan.label,
                      })
                    : t("quotaAtLimitDescMax", {
                        limit: quota?.limit ?? 1,
                        planName,
                      })
                }
                className="rounded-full bg-lime px-5 py-2.5 text-sm font-bold text-ink transition hover:brightness-95"
              >
                {t("newCta")}
              </button>
            ) : (
              <Link
                to="/postings/new"
                className="rounded-full bg-lime px-5 py-2.5 text-sm font-bold text-ink transition hover:brightness-95"
              >
                {t("newCta")}
              </Link>
            )
          }
        />

        {/* Quota lives in the right rail on desktop; on mobile the rail is
            hidden, so surface the same card inline under the header. Hidden for
            FREE (single slot — the count is noise). */}
        {isFree ? null : (
          <PostingQuotaCard quota={quota} className="lg:hidden" />
        )}

        {atLimit ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold">
              {t("quotaAtLimitTitle", { limit: quota?.limit ?? 0 })}
            </p>
            <p className="mt-0.5 text-amber-800">
              {nextPlan
                ? t("quotaAtLimitDesc", {
                    limit: quota?.limit ?? 1,
                    planName,
                    nextPlan: nextPlan.label,
                  })
                : t("quotaAtLimitDescMax", {
                    limit: quota?.limit ?? 1,
                    planName,
                  })}{" "}
              {nextPlan ? (
                <button
                  type="button"
                  onClick={() => upgradeTo(nextPlan.code)}
                  className="font-semibold underline underline-offset-2"
                >
                  {t("quotaAtLimitUpgradeCta", { nextPlan: nextPlan.label })}
                </button>
              ) : null}
              {quota && !quota.verified ? (
                <>
                  {" "}
                  <Link
                    to="/org/kyc"
                    className="font-semibold underline underline-offset-2"
                  >
                    {t("quotaVerifyNudge")}
                  </Link>
                </>
              ) : null}
            </p>
          </div>
        ) : null}

        <div className="flex gap-1 overflow-x-auto border-b border-smoke">
          {FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={cn(
                  "relative whitespace-nowrap px-3 py-2.5 text-sm font-semibold transition-colors",
                  active ? "text-ink" : "text-ash hover:text-ink",
                )}
              >
                {t(f.labelKey)}
                <span
                  className={cn(
                    "ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums",
                    active ? "bg-lime text-ink" : "bg-mist text-ash",
                  )}
                >
                  {counts[f.value]}
                </span>
                {active ? (
                  <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-lime" />
                ) : null}
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="h-7 w-7" />}
            title={t("empty")}
            description={t("emptyDesc")}
          />
        ) : (
          <div className="space-y-6">
            {BUCKET_ORDER.map((bucket) => {
              const items = buckets[bucket];
              if (items.length === 0) return null;
              return (
                <section key={bucket} className="space-y-2">
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-ink/60">
                    <span>{t(BUCKET_LABEL_KEY[bucket])}</span>
                    <span className="rounded-full bg-smoke px-2 py-0.5 text-[10px] text-ink/70">
                      {items.length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {items.map((p) => (
                      <PostingHealthCard
                        key={p.id}
                        posting={p}
                        tips={tipsByPosting.get(p.id) ?? []}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {/* Rail is desktop-only — on mobile the same signals (credits, tips)
          surface elsewhere (dashboard, profile), and the list owns the full
          viewport so cards stay readable on phone width. */}
      <div className="hidden lg:block">
        <InsightRail orgId={activeOrgId} />
      </div>
    </div>
  );
}

function groupByBucket(items: Posting[]): Record<HealthBucket, Posting[]> {
  const out: Record<HealthBucket, Posting[]> = {
    needs_attention: [],
    running: [],
    closed: [],
  };
  for (const p of items) {
    const bucket = bucketFromHealth(derivePostingHealth(p));
    out[bucket].push(p);
  }
  return out;
}
